import React, { useState } from "react";
import { Form, FormGroup, Label, Input, CustomInput, Button } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { TecAssuredStore } from "../../../../../../stores/TecAssuredStore";
import {
	createAgreement,
	getProductProperties,
} from "../../../../../../api/TecAssuredAPI";
import { useFromUser } from "../../../../../../stores/LocalStorageHelper";
import useCurrentDealStore from "../../../../../../stores/DealStore";
import shallow from "zustand/shallow";

const TecAssuredQuotes = ({ onComplete }) => {
	const { handleSubmit, control } = useForm({ mode: "onBlur" });
	const [selectedProductKey, setSelectedProductKey] = useState("");

	const {
		quoteData,
		setCreateAgreementReqBody,
		setProductPropertiesResp,
		setAgreementReqBody,
	} = TecAssuredStore();

	const locationID = useFromUser("locationID");

	const { buyer, buyerAddress, coBuyer, coBuyerAddress, lender, deal } =
		useCurrentDealStore(
			(state) => ({
				buyer: state.buyer,
				buyerAddress: state.buyerAddress,
				coBuyer: state.coBuyer,
				coBuyerAddress: state.coBuyerAddress,
				lender: state.lender,
				deal: state.deal,
			}),
			shallow
		);

	if (!quoteData) return <p>Loading quote data...</p>;

	const formatCurrency = (amount) => {
		const num = parseFloat(amount);
		return isNaN(num)
			? amount
			: new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: "USD",
			  }).format(num);
	};

	const flatProductOptions = quoteData.ProductTypes.flatMap((ptype) => {
		const products = Array.isArray(ptype.Products)
			? ptype.Products
			: [ptype.Products];
		return products.map((prod) => ({
			key: `${ptype.ProductTypeID}_${prod.ProductID}`,
			label: `${ptype.ProductType} - ${prod.Product}`,
			product: prod,
			productType: ptype,
		}));
	});

	const selectedMeta = flatProductOptions.find(
		(opt) => opt.key === selectedProductKey
	);

	const handleFormSubmit = (formData) => {
		if (!selectedMeta) return;

		const key = selectedMeta.key;
		const { product, productType } = selectedMeta;
		const selectedRate = formData?.[`${key}_rate`];
		const selectedOptions = formData?.[`${key}_options`] || [];

		const matchedRate = Array.isArray(product.Rates)
			? product.Rates.find((r) => r.VariantID === selectedRate)
			: product.Rates?.VariantID === selectedRate
			? product.Rates
			: null;

		const filteredProduct = {
			...product,
			Rates: matchedRate ? [matchedRate] : [],
			Options: (product.Options || []).filter((o) =>
				selectedOptions.includes(o.RateOptionID)
			),
			Surcharges: product.Surcharges || [],
		};

		const requestBody = {
			locationId: locationID,
			RatePackage: {
				...quoteData,
				ProductTypes: [
					{
						...productType,
						Products: [filteredProduct],
					},
				],
			},
		};

		console.log("Final createAgreement body:", requestBody);
		setCreateAgreementReqBody(requestBody);

		Promise.all([
			createAgreement(requestBody),
			getProductProperties({
				locationId: locationID,
				productTypeId: productType.ProductTypeID,
			}),
		]).then((res) => {
			const Lienholder = {
				LienholderID: "-1",
				FinanceNumber: lender?.lenderNo,
				Lienholder: lender?.name,
				Address: lender?.street,
				Address2: null,
				City: lender?.city,
				State: lender?.state,
				Country: "US",
				Postal: lender?.zip,
				Contact: lender?.contact1,
				Phone: lender?.phone1,
				Fax: null,
				EMail: lender?.email,
			};

			const Person = {
				FirstName: buyer?.firstName,
				LastName: buyer?.lastName,
				Address: buyerAddress?.street,
				Address2: buyerAddress?.unit,
				City: buyerAddress?.city,
				Region: buyerAddress?.state,
				Country: "US",
				PostalCode: buyerAddress?.zip,
				HomePhone: buyer?.homePhone,
				OtherPhone: null,
				MobilePhone: buyer?.cellPhone,
				EMail: buyer?.email,
			};

			const Coperson = {
				FirstName: coBuyer?.firstName,
				LastName: coBuyer?.lastName,
				Address: coBuyerAddress?.street,
				Address2: coBuyerAddress?.unit,
				City: coBuyerAddress?.city,
				Region: coBuyerAddress?.state,
				Country: "US",
				PostalCode: coBuyerAddress?.zip,
				HomePhone: coBuyer?.homePhone,
				OtherPhone: null,
				MobilePhone: coBuyer?.cellPhone,
				EMail: coBuyer?.email,
			};

			let agreement = {
				...res[0].data,
				Person,
			};

			if (deal.coBuyerRecNum !== null) {
				agreement.Coperson = Coperson;
			}
			if (deal.lenderRecNum !== null) {
				agreement.Lienholder = Lienholder;
			}

			setAgreementReqBody({
				locationId: locationID,
				Agreement: agreement,
			});

			setProductPropertiesResp({
				...res[1].data,
				item: res[1].data?.item || [],
			});

			onComplete();
		});
	};

	return (
		<Form onSubmit={handleSubmit(handleFormSubmit)}>
			<FormGroup>
				<Label for="selectedProductKey">Select a Product</Label>
				<Controller
					name="selectedProductKey"
					control={control}
					defaultValue=""
					render={({ onChange }) => (
						<Input
							id="selectedProductKey"
							type="select"
							value={selectedProductKey}
							onChange={(e) => {
								setSelectedProductKey(e.target.value);
								onChange(e.target.value);
							}}
						>
							<option value="">Select a product</option>
							{flatProductOptions.map((opt) => (
								<option key={opt.key} value={opt.key}>
									{opt.label}
								</option>
							))}
						</Input>
					)}
				/>
			</FormGroup>

			{selectedMeta && (
				<div className="ml-2 mt-3">
					{/* Rate dropdown */}
					<FormGroup>
						<Label for={`rate_${selectedMeta.product.ProductID}`}>Rate</Label>
						<Controller
							name={`${selectedMeta.key}_rate`}
							control={control}
							defaultValue=""
							render={({ onChange, value }) => {
								const rates = Array.isArray(selectedMeta.product.Rates)
									? selectedMeta.product.Rates
									: [selectedMeta.product.Rates];
								return (
									<Input
										type="select"
										value={value}
										onChange={(e) => onChange(e.target.value)}
										id={`rate_${selectedMeta.product.ProductID}`}
									>
										<option value="">Select a rate</option>
										{rates.map((rate) => (
											<option key={rate.VariantID} value={rate.VariantID}>
												{rate.Variant} - {formatCurrency(rate.Amount)}
											</option>
										))}
									</Input>
								);
							}}
						/>
					</FormGroup>

					{/* Options */}
					{selectedMeta.product.Options?.length > 0 && (
						<FormGroup>
							<Label>Options</Label>
							<Controller
								name={`${selectedMeta.key}_options`}
								control={control}
								defaultValue={[]}
								render={({ onChange, value }) => (
									<>
										{selectedMeta.product.Options.map((opt) => (
											<CustomInput
												key={opt.RateOptionID}
												type="checkbox"
												id={`opt_${opt.RateOptionID}`}
												label={`${opt.RateOption} - ${formatCurrency(
													opt.Amount
												)}`}
												checked={value?.includes(opt.RateOptionID)}
												onChange={(e) => {
													const newVal = e.target.checked
														? [...value, opt.RateOptionID]
														: value.filter((id) => id !== opt.RateOptionID);
													onChange(newVal);
												}}
											/>
										))}
									</>
								)}
							/>
						</FormGroup>
					)}

					{/* Surcharges */}
					{selectedMeta.product.Surcharges?.length > 0 && (
						<FormGroup>
							<Label>Surcharges</Label>
							<ul className="mb-2">
								{selectedMeta.product.Surcharges.map((sc) => (
									<li key={sc.RateOptionID}>
										{`${sc.RateOption} - ${formatCurrency(sc.Amount)}`}
									</li>
								))}
							</ul>
						</FormGroup>
					)}
				</div>
			)}

			<Button type="submit" color="primary">
				Continue
			</Button>
		</Form>
	);
};

export default TecAssuredQuotes;
